import * as React from "react";
import { useState, useCallback, useRef } from "react";
// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
import { copyIconProps, editIconProps, deleteIconProps, moreIconProps, errorIconProps } from "../../../../icons/icons.styles";
// Components
import { ContextualMenu } from "../../../contextualMenu/ContextualMenu";
import { DocumentPreviewIcon } from "../../documentPreviewIcon/DocumentPreviewIcon";
import { Checkbox, DirectionalHint, Icon, ProgressIndicator, useTheme } from "@fluentui/react";
// Styles
import { getClassNames, getStyles } from "./TaskAttachmentRow.styles";
// Utilities
import { css } from "@fluentui/utilities";
import { safelyOpenExternalUrl } from "@ms/uno-utilities/lib/local/BrowserUtilities";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import { useLoggers } from "../../../../hooks/useLoggers";
export function TaskAttachmentRow(props) {
    const { taskAttachmentRowItem, taskAttachmentRowStrings, documentPreviewIcons, config, callbacks, icons, taskAttachmentIconRef, taskAttachmentMenuButtonRef, enableExternalUrlRegexCheck } = props;
    const theme = useTheme();
    const isUrlValid = taskAttachmentRowItem.isUrlValid();
    const ariaLabel = isUrlValid ? taskAttachmentRowItem.alias : `${taskAttachmentRowItem.alias}. ${taskAttachmentRowStrings.invalidLinkError}`;
    const [isMoreMenuVisible, setIsMoreMenuVisible] = useState(false);
    const taskAttachmentIcon = useRef(null);
    const taskAttachmentMenuButton = useRef(null);
    const styles = getStyles({
        uploading: taskAttachmentRowItem.isBeingUploaded,
        unclickable: !isUrlValid
    }, props.styles);
    const classNames = getClassNames(styles);
    const { traceLogger } = useLoggers();
    const previewIcon = /*#__PURE__*/ React.createElement(DocumentPreviewIcon, {
        key: taskAttachmentRowItem.url,
        taskAttachmentRowItem: taskAttachmentRowItem,
        className: classNames.fallbackPreviewIcon,
        icons: documentPreviewIcons
    });
    /**
     * Opens the current task attachment in a new tab
     */ const openAttachment = useCallback(()=>{
        if (!taskAttachmentRowItem.isBeingUploaded && taskAttachmentRowItem.isUrlValid()) {
            callbacks.onOpenLink(taskAttachmentRowItem);
        }
    }, [
        callbacks,
        taskAttachmentRowItem
    ]);
    /**
     * Handles the keydown event and opens the task attachment if enter was hit
     * @param event Keydown event
     */ const onKeyDown = useCallback((event)=>{
        const key = KeyboardUtilities.getKey(event);
        // Open attachment if enter or space is pressed for accessibility
        if (key === KeyCode.ENTER || key === KeyCode.SPACE) {
            openAttachment();
            return;
        }
    }, [
        openAttachment
    ]);
    /**
     * Render the invalid link logo
     */ const renderInvalidLinkIcon = ()=>{
        return /*#__PURE__*/ React.createElement("span", {
            className: classNames.brokenLinkIcon,
            title: taskAttachmentRowStrings.invalidLinkError
        }, /*#__PURE__*/ React.createElement(Icon, icons?.alert ?? errorIconProps));
    };
    /**
     * Render the action button
     */ const renderActionButton = ()=>{
        if (taskAttachmentRowItem.isBeingUploaded) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("button", {
            className: classNames.actionButton,
            onClick: openAttachment,
            "aria-label": taskAttachmentRowStrings.actionButtonLabel
        }, taskAttachmentRowStrings.actionButtonLabel);
    };
    /**
     * Render the more options button
     */ const renderMoreOptions = ()=>{
        if (taskAttachmentRowItem.isBeingUploaded || config.hideMoreButton) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("button", {
            className: classNames.attachmentMoreButton,
            onClick: (evt)=>{
                evt.stopPropagation();
                setIsMoreMenuVisible(true);
            },
            onKeyDown: (evt)=>{
                const key = KeyboardUtilities.getKey(evt);
                if (key === KeyCode.ENTER || key === KeyCode.SPACE) {
                    evt.stopPropagation();
                    setIsMoreMenuVisible(true);
                }
            },
            "aria-label": taskAttachmentRowStrings.moreOptionsAriaLabel,
            "aria-haspopup": true,
            ref: taskAttachmentMenuButton
        }, /*#__PURE__*/ React.createElement(Icon, icons?.more ?? moreIconProps));
    };
    /**
     * Open the task attachment in a new window
     * @param evt UI Event encapsulating details of the user click/keypress event
     */ const onOpenInNewWindow = useCallback((evt)=>{
        const { taskAttachmentRowItem } = props;
        if (!taskAttachmentRowItem.isBeingUploaded && taskAttachmentRowItem.isUrlValid()) {
            safelyOpenExternalUrl(taskAttachmentRowItem.url, traceLogger, enableExternalUrlRegexCheck);
        }
    }, [
        props,
        traceLogger,
        enableExternalUrlRegexCheck
    ]);
    /**
     * Display the command drop down for preview list
     */ const renderMoreContextMenu = ()=>{
        if (!isMoreMenuVisible) {
            return null;
        }
        const menuItems = [];
        if (!config.hideOpenInNewWindow) {
            menuItems.push({
                key: "OpenInNewWindow",
                name: taskAttachmentRowStrings.openInNewWindowMenuOption,
                onClick: onOpenInNewWindow
            });
        }
        if (!config.disableCopyLink) {
            menuItems.push({
                key: "CopyLink",
                name: taskAttachmentRowStrings.copyLinkOption,
                onClick: (evt)=>{
                    callbacks.onCopyLink(taskAttachmentRowItem);
                },
                iconProps: {
                    ...icons?.copy ?? copyIconProps
                }
            });
        }
        menuItems.push({
            key: "Edit",
            name: taskAttachmentRowStrings.editMenuOption,
            onClick: (evt)=>{
                props.callbacks.onEdit();
            },
            disabled: config.disableEdit,
            iconProps: {
                ...icons?.edit ?? editIconProps
            }
        });
        menuItems.push({
            key: "Remove",
            name: taskAttachmentRowStrings.removeMenuOption,
            onClick: callbacks.onDelete,
            disabled: config.disableRemove,
            iconProps: {
                ...icons?.remove ?? deleteIconProps
            }
        });
        return /*#__PURE__*/ React.createElement(ContextualMenu, {
            shouldFocusOnMount: true,
            items: menuItems,
            directionalHint: DirectionalHint.bottomAutoEdge,
            target: taskAttachmentMenuButton,
            onDismiss: ()=>setIsMoreMenuVisible(false),
            styles: styles.subComponentStyles.contextualMenu,
            theme: theme
        });
    };
    /**
     * Render the progress indicator
     */ const renderProgressIndicator = ()=>{
        if (taskAttachmentRowItem.isBeingUploaded) {
            return /*#__PURE__*/ React.createElement(ProgressIndicator, {
                styles: styles.subComponentStyles.uploadProgress,
                percentComplete: taskAttachmentRowItem.uploadProgress
            });
        }
        return null;
    };
    /**
     * Render the show on card checkbox
     */ const renderShowOnCard = ()=>{
        if (config.hideShowOnCardButton) {
            return null;
        }
        const className = css({
            visible: taskAttachmentRowItem.isShownInTaskCard
        });
        const ariaLabel = taskAttachmentRowStrings.showOnCardAriaLabel(taskAttachmentRowItem.alias);
        return /*#__PURE__*/ React.createElement(Checkbox, {
            className: className,
            styles: styles.subComponentStyles.showOnCard,
            checked: taskAttachmentRowItem.isShownInTaskCard,
            label: taskAttachmentRowStrings.showOnCardLabel,
            onChange: (ev, checked)=>{
                callbacks.onSelectDeselectShowOnCard(!!checked);
            },
            ariaLabel: ariaLabel,
            disabled: config.disableEdit
        });
    };
    React.useEffect(()=>{
        // Setting the refs for the task attachment icon and the task attachment menu button
        taskAttachmentIcon.current && taskAttachmentIconRef(taskAttachmentIcon.current);
        taskAttachmentMenuButton.current && taskAttachmentMenuButtonRef(taskAttachmentMenuButton.current);
    }, [
        taskAttachmentIconRef,
        taskAttachmentMenuButtonRef,
        taskAttachmentIcon,
        taskAttachmentMenuButton
    ]);
    return /*#__PURE__*/ React.createElement("li", {
        className: classNames.root,
        key: `taskAttachmentRow${taskAttachmentRowItem.url}`
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.content
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.leftContent
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.attachmentChicklet,
        "aria-label": ariaLabel,
        tabIndex: 0,
        role: "link",
        onKeyDown: onKeyDown
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.previewListImage,
        onClick: openAttachment,
        ref: taskAttachmentIcon
    }, previewIcon), /*#__PURE__*/ React.createElement("div", {
        className: classNames.itemListTitleContainer,
        onClick: openAttachment
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.itemListTitle,
        title: taskAttachmentRowItem.alias
    }, /*#__PURE__*/ React.createElement("span", {
        className: classNames.itemListTitleText,
        title: taskAttachmentRowItem.alias
    }, taskAttachmentRowItem.alias)), /*#__PURE__*/ React.createElement("div", {
        className: classNames.itemListUrl,
        title: taskAttachmentRowItem.url
    }, /*#__PURE__*/ React.createElement("span", {
        className: classNames.itemListUrlText,
        title: taskAttachmentRowItem.url
    }, taskAttachmentRowItem.url))), !isUrlValid && renderInvalidLinkIcon(), !config.disableActionButton && /*#__PURE__*/ React.createElement("div", null, renderActionButton()), /*#__PURE__*/ React.createElement("div", {
        className: classNames.otherCommands
    }, renderMoreOptions(), renderMoreContextMenu())), renderProgressIndicator()), renderShowOnCard()));
}
